import React, { useState, useEffect } from 'react';
import { Card, Form, InputNumber, Switch, Button, message, Divider, Space, Typography, Select } from 'antd';
import { SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import API from '../api';

const { Title, Text } = Typography;

export default function Config() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // A30: Platform config UI
  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await API.get('/config/platform');
      form.setFieldsValue(res.data);
    } catch (err) {
      console.error(err);
      message.error('Failed to load configuration (API may be missing)');
      // Mock defaults for A16, A17, A30
      form.setFieldsValue({
        commissionRateBasic: 20,
        commissionRatePro: 10,
        commissionRateBusiness: 5,
        trustSafetyFee: 15.00,
        cancellationFeePercent: 10,
        payoutThreshold: 150.00,
        requestTimeoutMinutes: 15,
        matchingRadiusKm: 15,
        allowCashPayments: true,
        minCalloutHousehold: 250,
        minCalloutProfessional: 400
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      await API.put('/config/platform', values);
      message.success('Platform configuration updated successfully');
    } catch (err) {
      console.error(err);
      message.error('Failed to update configuration');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 800, margin: '0 auto' }}>
      <Space style={{ marginBottom: 24, justifyContent: 'space-between', width: '100%' }}>
        <Title level={2} style={{ margin: 0 }}>Platform Configuration</Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchConfig} loading={loading}>Reset</Button>
          <Button type="primary" icon={<SaveOutlined />} onClick={() => form.submit()} loading={loading}>
            Save Changes
          </Button>
        </Space>
      </Space>

      <Form form={form} layout="vertical" onFinish={onFinish}>
        {/* A16: Fee configuration */}
        <Card title="Fee & Commission Configuration" style={{ marginBottom: 24 }}>
          <Space size="large" align="start">
            <Form.Item label="Basic Tier Commission (%)" name="commissionRateBasic" rules={[{ required: true }]}>
              <InputNumber min={0} max={100} addonAfter="%" />
            </Form.Item>
            <Form.Item label="Pro Tier Commission (%)" name="commissionRatePro" rules={[{ required: true }]}>
              <InputNumber min={0} max={100} addonAfter="%" />
            </Form.Item>
            <Form.Item label="Business Tier Commission (%)" name="commissionRateBusiness" rules={[{ required: true }]}>
              <InputNumber min={0} max={100} addonAfter="%" />
            </Form.Item>
          </Space>
          <Divider />
          <Space size="large" align="start">
            <Form.Item label="Trust & Safety Fee" name="trustSafetyFee" rules={[{ required: true }]}>
              <InputNumber min={0} prefix="R" precision={2} />
            </Form.Item>
            <Form.Item label="Cancellation Fee (%)" name="cancellationFeePercent" rules={[{ required: true }]} tooltip="Applied if cancelled after provider acceptance">
              <InputNumber min={0} max={100} addonAfter="%" />
            </Form.Item>
            <Form.Item label="Minimum Payout Threshold" name="payoutThreshold" rules={[{ required: true }]}>
              <InputNumber min={0} prefix="R" precision={2} />
            </Form.Item>
          </Space>
        </Card>

        {/* A17: Minimum call-out rates */}
        <Card title="Minimum Call-Out Rates (ZAR)" style={{ marginBottom: 24 }}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            Set the absolute minimum budget a customer can enter when creating a request in these category groups.
          </Text>
          <Space size="large" wrap>
            <Form.Item label="Household / Cleaning" name="minCalloutHousehold" rules={[{ required: true }]}>
              <InputNumber min={0} prefix="R" />
            </Form.Item>
            <Form.Item label="Professional Services" name="minCalloutProfessional" rules={[{ required: true }]}>
              <InputNumber min={0} prefix="R" />
            </Form.Item>
            <Form.Item label="Garden & Outdoor" name="minCalloutGarden">
              <InputNumber min={0} prefix="R" />
            </Form.Item>
            <Form.Item label="Health & Beauty" name="minCalloutHealth">
              <InputNumber min={0} prefix="R" />
            </Form.Item>
          </Space>
        </Card>

        {/* A30: Additional Platform settings */}
        <Card title="Operational Settings" style={{ marginBottom: 24 }}>
          <Space size="large" wrap align="start">
            <Form.Item label="Request Auto-Timeout" name="requestTimeoutMinutes" rules={[{ required: true }]} tooltip="Minutes before an unaccepted PENDING request expires">
              <InputNumber min={1} max={1440} addonAfter="min" />
            </Form.Item>
            <Form.Item label="Default Matching Radius" name="matchingRadiusKm" rules={[{ required: true }]} tooltip="Default provider search radius for jobs">
              <InputNumber min={1} max={100} addonAfter="km" />
            </Form.Item>
            <Form.Item label="Allow Cash Payments" name="allowCashPayments" valuePropName="checked">
              <Switch checkedChildren="Yes" unCheckedChildren="No" />
            </Form.Item>
          </Space>
        </Card>
      </Form>
    </div>
  );
}
